<template>
  <transition name="modal-fade">
    <div v-if="modelValue" class="modal-overlay" @click.self="closeModal">
      <div class="modal-container">
        <div class="modal-header">
          <h2 class="modal-title">
            <IconSvg name="copy-solid-full" size="md" /> しおりを複製
          </h2>
          <button
            type="button"
            class="modal-close-btn"
            :disabled="isSubmitting"
            aria-label="閉じる"
            @click="closeModal"
          >
            &times;
          </button>
        </div>

        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>

        <form class="modal-form" @submit.prevent="handleCopy">
          <div class="form-group">
            <label class="form-label">
              新しいタイトル <span class="required">*</span>
            </label>
            <input
              v-model="newTitle"
              type="text"
              class="form-input"
              placeholder="新しいしおりのタイトル"
              maxlength="255"
              :disabled="isSubmitting"
            />
            <p v-if="titleError" class="error-text">{{ titleError }}</p>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              キャンセル
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? "複製中..." : "複製" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import IconSvg from "@/components/IconSvg.vue";
import { useShioriStore } from "@/store/shiori-store.js";

export default {
  name: "CopyShioriModal",
  components: { IconSvg },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    shioriData: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:modelValue", "copied"],
  setup(props, { emit }) {
    const router = useRouter();
    const shioriStore = useShioriStore();
    const newTitle = ref("");
    const titleError = ref("");
    const errorMessage = ref("");
    const successMessage = ref("");
    const isSubmitting = ref(false);

    const clearMessages = () => {
      titleError.value = "";
      errorMessage.value = "";
      successMessage.value = "";
    };

    watch(
      () => props.modelValue,
      (isOpen) => {
        if (isOpen && props.shioriData) {
          newTitle.value = `${props.shioriData.SUBJECT_NMV}のコピー`;
          clearMessages();
        }
      },
      { immediate: true },
    );

    const closeModal = () => {
      if (isSubmitting.value) return;
      emit("update:modelValue", false);
      clearMessages();
    };

    const validateTitle = () => {
      titleError.value = "";
      const title = newTitle.value.trim();
      if (!title) {
        titleError.value = "タイトルを入力してください";
        return false;
      }
      if (title.length > 255) {
        titleError.value = "タイトルは255文字以内で入力してください";
        return false;
      }
      return true;
    };

    const handleCopy = async () => {
      if (!validateTitle()) return;

      isSubmitting.value = true;
      errorMessage.value = "";
      successMessage.value = "";

      try {
        const result = await shioriStore.copyShiori(
          props.shioriData.SHIORI_NOV,
          newTitle.value.trim(),
        );
        successMessage.value = "しおりを複製しました";
        emit("copied", result);

        setTimeout(() => {
          emit("update:modelValue", false);
          router.push(`/shiori/${result.shiori_id}`);
        }, 500);
      } catch (error) {
        console.error("しおり複製エラー:", error);
        errorMessage.value =
          error.response?.data?.detail ||
          "しおりの複製に失敗しました。もう一度お試しください。";
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      newTitle,
      titleError,
      errorMessage,
      successMessage,
      isSubmitting,
      closeModal,
      handleCopy,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
}

.modal-container {
  width: min(90%, 500px);
  max-height: calc(100dvh - 1rem);
  overflow-y: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-title {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin: 0;
  color: #111827;
  font-size: 1.25rem;
}

.modal-close-btn {
  width: 2rem;
  height: 2rem;
  padding: 0;
  color: #6b7280;
  font-size: 2rem;
  line-height: 1;
  background: none;
  border: 0;
  cursor: pointer;
}

.modal-form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #374151;
  font-weight: 500;
}

.required,
.error-text {
  color: #dc2626;
}

.form-input {
  box-sizing: border-box;
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
}

.error-text {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
}

.error-message,
.success-message {
  margin: 1rem 1.5rem 0;
  padding: 1rem;
  border-radius: 4px;
}

.error-message {
  color: #991b1b;
  background: #fee2e2;
  border-left: 4px solid #dc2626;
}

.success-message {
  color: #065f46;
  background: #d1fae5;
  border-left: 4px solid #059669;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  color: #374151;
  background: #f3f4f6;
}

.btn-primary {
  color: white;
  background: #2563eb;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
